/**
 * Value Creation Detail Component
 * 
 * Provides detailed breakdown of value creation metrics with historical and projection data.
 * Shows:
 * - Value ratio with current metrics
 * - Entropy score with objectives to reduce coordination loss (1-10 scale, lower=better)
 * - Combined bar chart showing Value Generated vs Cost to Generate across all periods
 * - Projections displayed as range bars with increasing uncertainty
 * 
 * @see docs/KRYSAIS_CONTROL_GLOSSARY.md for terminology definitions
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, Tag } from 'antd';
import {
  TeamOutlined,
  DollarOutlined,
  UserOutlined,
  ThunderboltOutlined,
  BarChartOutlined,
  RightOutlined
} from '@ant-design/icons';
import { BaseReportingComponent, ComponentData, ReportingComponentProps, PeriodData } from './BaseReportingComponent';
import { ValueMetricDisplay, getEntropyInfo, ValueType } from '../../Shared/ValueMetricDisplay';
import { ValueBreakdownChart, ChartPeriodData } from './ValueBreakdownChart';
import styles from './ValueCreationDetailComponent.module.css';

// Objective can be a string or an object with ID
export type ObjectiveItem = string | { id: string; text: string };

interface ValueDetailSection {
  type: 'stakeholder' | 'business' | 'customer';
  label: string;
  icon: React.ReactNode;
  color: string;
  valueRatio: number;
  /** Entropy score (1-10, lower=better). Measures coordination loss. */
  entropyScore: number;
  valueGenerated: number;
  costToGenerate: number;
  objectives?: ObjectiveItem[];
}

type ValueTypeKey = 'stakeholder' | 'business' | 'customer';

interface ValueCreationDetailComponentProps extends Omit<ReportingComponentProps, 'componentType'> {
  /** Default active tab (when showTabs is true) */
  defaultActiveTab?: ValueTypeKey;
  /** Lock to a specific value type (hides tabs) */
  lockedValueType?: ValueTypeKey;
  /** Show the header */
  showHeader?: boolean;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Convert backend assessment_rank (higher=better) to entropy score (lower=better)
const toEntropyScore = (assessmentRank: number): number => 11 - assessmentRank;

const getValueDetails = (data: Record<string, any>): ValueDetailSection[] => {
  return [
    {
      type: 'stakeholder',
      label: 'Stakeholder Value',
      icon: <TeamOutlined />,
      color: '#8b5cf6',
      valueRatio: data.stakeholder_value_ratio ?? 0,
      entropyScore: toEntropyScore(data.stakeholder_assessment_rank ?? 5),
      valueGenerated: data.stakeholder_value_generated ?? 0,
      costToGenerate: data.stakeholder_cost_to_generate ?? 0,
      objectives: data.stakeholder_objectives ?? [],
    },
    {
      type: 'business',
      label: 'Business Value',
      icon: <DollarOutlined />,
      color: '#10b981',
      valueRatio: data.business_value_ratio ?? 0,
      entropyScore: toEntropyScore(data.business_assessment_rank ?? 5),
      valueGenerated: data.business_value_generated ?? data.business_revenue ?? 0,
      costToGenerate: data.business_cost_to_generate ?? data.business_expenses ?? 0,
      objectives: data.business_objectives ?? [],
    },
    {
      type: 'customer',
      label: 'Customer Value',
      icon: <UserOutlined />,
      color: '#3b82f6',
      valueRatio: data.customer_value_ratio ?? 0,
      entropyScore: toEntropyScore(data.customer_assessment_rank ?? 5),
      valueGenerated: data.customer_value_generated ?? 0,
      costToGenerate: data.customer_cost_to_generate ?? 0,
      objectives: data.customer_objectives ?? [],
    }
  ];
};

/**
 * Transform period data into chart format
 */
const transformToChartData = (
  historicalData: PeriodData[],
  projectionData: PeriodData[],
  currentData: Record<string, any>,
  valueType: string
): ChartPeriodData[] => {
  const chartData: ChartPeriodData[] = [];

  // Add historical periods
  historicalData.forEach(period => {
    chartData.push({
      periodId: period.item_id,
      periodName: period.item_name,
      startDate: period.start_date || '',
      endDate: period.end_date || '',
      isProjected: false,
      isCurrent: false,
      valueGenerated: period.values[`${valueType}_value_generated`] ?? 0,
      costToGenerate: period.values[`${valueType}_cost_to_generate`] ?? 0,
    });
  });

  // Add current period
  chartData.push({
    periodId: 'current',
    periodName: 'Current',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    isProjected: false,
    isCurrent: true,
    valueGenerated: currentData[`${valueType}_value_generated`] ?? 0,
    costToGenerate: currentData[`${valueType}_cost_to_generate`] ?? 0,
  });

  // Add projection periods with ranges
  projectionData.forEach(period => {
    chartData.push({
      periodId: period.item_id,
      periodName: period.item_name,
      startDate: period.start_date || '',
      endDate: period.end_date || '',
      isProjected: true,
      isCurrent: false,
      // Projection ranges
      valueGeneratedLow: period.values[`${valueType}_value_generated_low`] ?? 0,
      valueGeneratedHigh: period.values[`${valueType}_value_generated_high`] ?? 0,
      costToGenerateLow: period.values[`${valueType}_cost_to_generate_low`] ?? 0,
      costToGenerateHigh: period.values[`${valueType}_cost_to_generate_high`] ?? 0,
    });
  });

  return chartData;
};

// Helper to extract objective ID and text from ObjectiveItem
const parseObjective = (objective: ObjectiveItem): { id: string | null; text: string } => {
  if (typeof objective === 'string') {
    // Check if string contains an ID pattern like "[obj-001] Improve..."
    const match = objective.match(/^\[([^\]]+)\]\s*(.+)$/);
    if (match) {
      return { id: match[1], text: match[2] };
    }
    // Generate a default ID from the text for demo purposes
    const defaultId = `obj-${objective.toLowerCase().replace(/[^a-z0-9]/g, '-').slice(0, 20)}`;
    return { id: defaultId, text: objective };
  }
  return { id: objective.id, text: objective.text };
};

const ValueDetailCard: React.FC<{ 
  section: ValueDetailSection;
  chartData: ChartPeriodData[];
  onObjectiveClick?: (objectiveId: string) => void;
}> = ({ section, chartData, onObjectiveClick }) => {
  const entropyInfo = getEntropyInfo(section.entropyScore);
  
  const handleObjectiveClick = (objective: ObjectiveItem) => {
    const { id } = parseObjective(objective);
    if (id && onObjectiveClick) {
      onObjectiveClick(id);
    }
  };
  
  return (
    <div className={styles.detailCard}>
      {/* Summary Metric Display */}
      <div className={styles.summaryMetric}>
        <ValueMetricDisplay
          valueRatio={section.valueRatio}
          entropyScore={section.entropyScore}
          valueType={section.type as ValueType}
          variant="detailed"
          showLabels={true}
          label={section.label}
        />
      </div>

      {/* Current Period Breakdown */}
      <div className={styles.currentBreakdown}>
        <div className={styles.breakdownItem}>
          <span className={styles.breakdownLabel}>Current Value Generated</span>
          <span className={styles.breakdownValue} style={{ color: '#10b981' }}>
            {formatCurrency(section.valueGenerated)}
          </span>
        </div>
        <div className={styles.breakdownDivider}>÷</div>
        <div className={styles.breakdownItem}>
          <span className={styles.breakdownLabel}>Current Cost</span>
          <span className={styles.breakdownValue} style={{ color: '#ef4444' }}>
            {formatCurrency(section.costToGenerate)}
          </span>
        </div>
        <div className={styles.breakdownDivider}>=</div>
        <div className={styles.breakdownItem}>
          <span className={styles.breakdownLabel}>Value Ratio</span>
          <span 
            className={styles.breakdownRatio}
            style={{ color: section.valueRatio >= 1 ? '#10b981' : '#ef4444' }}
          >
            {section.valueRatio.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Value Breakdown Chart - Historical + Current + Projections */}
      <div className={styles.chartSection}>
        <h4 className={styles.sectionTitle}>
          <BarChartOutlined /> Value Breakdown Over Time
        </h4>
        <ValueBreakdownChart
          data={chartData}
          height={320}
          valueColor="#10b981"
          costColor="#ef4444"
          valueLabel="Value Generated"
          costLabel="Cost to Generate"
        />
      </div>

      {/* Entropy Score & Objectives */}
      <div className={styles.assessmentSection}>
        <h4 className={styles.sectionTitle}>
          <ThunderboltOutlined /> Reduce Entropy
        </h4>
        
        <div className={styles.rankProgress}>
          <div className={styles.rankCurrent}>
            <Tag color={entropyInfo.color} className={styles.rankTag}>
              Entropy {section.entropyScore} - {entropyInfo.level}
            </Tag>
          </div>
          
          {entropyInfo.nextScore && (
            <div className={styles.nextRankInfo}>
              <span className={styles.nextRankLabel}>Target:</span>
              <span className={styles.nextRankValue}>
                Score {entropyInfo.nextScore} ({entropyInfo.nextLevel})
              </span>
            </div>
          )}
        </div>

        {/* Objectives - Clickable */}
        {section.objectives && section.objectives.length > 0 && (
          <div className={styles.objectivesSection}>
            <h5 className={styles.objectivesTitle}>Complete to Improve Coordination</h5>
            <ul className={styles.objectivesList}>
              {section.objectives.map((objective, index) => {
                const { text, id } = parseObjective(objective);
                const isClickable = !!id && !!onObjectiveClick;
                return (
                  <li 
                    key={index} 
                    className={`${styles.objectiveItem} ${isClickable ? styles.clickable : ''}`}
                    onClick={isClickable ? () => handleObjectiveClick(objective) : undefined}
                    role={isClickable ? 'button' : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    onKeyDown={isClickable ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleObjectiveClick(objective);
                      }
                    } : undefined}
                  >
                    <span className={styles.objectiveText}>{text}</span>
                    {isClickable && <RightOutlined className={styles.objectiveArrow} />}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export const ValueCreationDetailComponent: React.FC<ValueCreationDetailComponentProps> = ({
  defaultActiveTab = 'business',
  lockedValueType,
  showHeader = true,
  ...props
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>(lockedValueType || defaultActiveTab);

  // Update activeTab if lockedValueType changes
  React.useEffect(() => {
    if (lockedValueType) {
      setActiveTab(lockedValueType);
    }
  }, [lockedValueType]);

  // Handle objective click - navigate to objective page
  const handleObjectiveClick = (objectiveId: string) => {
    navigate(`/dashboard/objective/${objectiveId}`);
  };

  const renderContent = (data: ComponentData, loading: boolean) => {
    const sections = getValueDetails(data.data);
    
    // If locked to a specific type, only show that section
    if (lockedValueType) {
      const lockedSection = sections.find(s => s.type === lockedValueType);
      if (!lockedSection) return null;

      const chartData = transformToChartData(
        data.historical_data,
        data.projection_data,
        data.data,
        lockedSection.type
      );

      return (
        <div className={`${styles.valueCreationDetail} ${styles.locked}`}>
          {showHeader && (
            <div className={styles.header}>
              <h3 className={styles.title}>
                <span className={styles.headerIcon} style={{ color: lockedSection.color }}>
                  {lockedSection.icon}
                </span>
                {lockedSection.label} Details
              </h3>
              <span className={styles.artifactName}>{data.artifact_name}</span>
            </div>
          )}

          <div className={styles.lockedContent}>
            <ValueDetailCard 
              section={lockedSection} 
              chartData={chartData}
              onObjectiveClick={handleObjectiveClick}
            />
          </div>
        </div>
      );
    }

    // Standard tabbed view
    const tabItems = sections.map(section => {
      // Transform data for this section's chart
      const chartData = transformToChartData(
        data.historical_data,
        data.projection_data,
        data.data,
        section.type
      );

      return {
        key: section.type,
        label: (
          <span className={styles.tabLabel}>
            <span className={styles.tabIcon} style={{ color: section.color }}>
              {section.icon}
            </span>
            {section.label}
          </span>
        ),
        children: (
          <div className={styles.tabContent}>
            <ValueDetailCard 
              section={section} 
              chartData={chartData}
              onObjectiveClick={handleObjectiveClick}
            />
          </div>
        )
      };
    });

    return (
      <div className={styles.valueCreationDetail}>
        {showHeader && (
          <div className={styles.header}>
            <h3 className={styles.title}>Value Creation Detail</h3>
            <span className={styles.artifactName}>{data.artifact_name}</span>
          </div>
        )}

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className={styles.tabs}
        />
      </div>
    );
  };

  return (
    <BaseReportingComponent
      {...props}
      componentType="value_creation_detail"
      includeHistorical={true}
    >
      {renderContent}
    </BaseReportingComponent>
  );
};

export default ValueCreationDetailComponent;
